import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { axiosInstance } from "../lib/axios";
import { useProposalStore } from "../store/useProposalStore";
import InfographicSectionEditor from "../components/proposals/builder/InfographicSectionEditor";
import InfographicTemplate from "../components/proposals/templates/InfographicTemplate";
import { getInfographicSection } from "../config/infographicTemplate";

/**
 * Proposal Review Screen — built to the approved concept
 * (cre-FE/reference/proposal-review-screen-concept.html).
 *
 * The proposal is reviewed inline, section by section: every block carries where
 * its content came from (AI-written vs reused from the library), the BD signs
 * off or edits it in place, and the same screen carries the combined history of
 * how the proposal came to be. Publishing happens from the top bar.
 *
 * Fixed light palette, like the client-facing proposal — a review screen that
 * changes colour with the operating system makes the preview a lie.
 */

// ── palette from the concept ────────────────────────────────────────────────
const C = {
  bg: "#EEF0F3",
  card: "#FFFFFF",
  line: "#E3E5EA",
  ink: "#15181C",
  inkSoft: "#565C66",
  inkFaint: "#8990A0",
  band: "#FAFBFA",
  moneyBand: "#F1F8F3",
  ai: "#C2760C",
  aiSoft: "#FCEBCB",
  aiBoxLine: "#F1D9A0",
  aiBox: "#FFFBF2",
  ok: "#2F855A",
  okSoft: "#DFF3E6",
  okLine: "#BFE3CF",
  nudge: "#6B5CA5",
  danger: "#B42318",
  dangerSoft: "#FEECEA",
  dangerLine: "#F5C6C2",
  mono: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
};

const DRAFT_TONE = { bg: "#EEF0F3", fg: C.inkSoft };
const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  Draft: DRAFT_TONE,
  "In Review": { bg: C.aiSoft, fg: C.ai },
  "Ready to Publish": { bg: "#E6EEFB", fg: "#1F4FA3" },
  Published: { bg: C.okSoft, fg: C.ok },
};

// Actor colours for the history timeline, straight from the concept.
const HUMAN_ACTOR = { label: "YOU", color: C.ok };
const ACTOR: Record<string, { label: string; color: string }> = {
  system: { label: "SYSTEM", color: "#6E7580" },
  ai: { label: "AI DRAFT", color: C.ai },
  human: HUMAN_ACTOR,
  nudge: { label: "NUDGE", color: C.nudge },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "system", label: "System" },
  { key: "ai", label: "AI-written" },
  { key: "human", label: "Your edits" },
  { key: "nudge", label: "Nudges" },
];

// Which actor an activity entry belongs to.
const actorOf = (action = "") => {
  const a = String(action).toLowerCase();
  if (a.includes("ai") || a.includes("draft")) return "ai";
  if (a.includes("created") || a.includes("matched") || a.includes("system")) return "system";
  if (a.includes("nudge")) return "nudge";
  return "human";
};

const timeOf = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  } catch {
    return "";
  }
};

// ── small presentational pieces ─────────────────────────────────────────────
const Pill = ({
  children,
  onClick,
  tone = "plain",
  disabled,
}: {
  children: any;
  onClick?: () => void;
  tone?: "plain" | "dark" | "ok";
  disabled?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      fontSize: 12,
      fontWeight: 700,
      borderRadius: 7,
      padding: "7px 12px",
      cursor: disabled ? "default" : "pointer",
      border: `1px solid ${tone === "dark" ? C.ink : C.line}`,
      background: tone === "dark" ? C.ink : C.card,
      color: tone === "dark" ? "#fff" : tone === "ok" ? C.ok : C.inkSoft,
      opacity: disabled ? 0.5 : 1,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

const Badge = ({ tone, children }: { tone: "ai" | "ok" | "manual"; children: any }) => (
  <span
    style={{
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: ".02em",
      padding: "4px 10px",
      borderRadius: 100,
      color: tone === "ai" ? C.ai : tone === "ok" ? C.ok : C.inkSoft,
      background: tone === "ai" ? C.aiSoft : tone === "ok" ? C.okSoft : "#EEF0F3",
    }}
  >
    {children}
  </span>
);

// These four are edited here but never appear as their own block on the client
// page — the first two are the hero, the last two sign the footer.
const DATA_ONLY = ["cover", "portfolio", "client-info", "contact"];
const DATA_ONLY_NOTE: Record<string, string> = {
  cover: "SHOWN AS THE HERO",
  portfolio: "SHOWN IN THE HERO SPOTLIGHT",
  "client-info": "NAMED IN THE FOOTER",
  contact: "SIGNS THE FOOTER",
};

type Device = "desktop" | "mobile";

const ProposalReviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchReview, publishProposal, isPublishing, updateProposal } = useProposalStore();

  const [data, setData] = useState<any>(null);
  const [proposal, setProposal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<"review" | "history" | "client">("review");
  const [device, setDevice] = useState<Device>("desktop");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftSection, setDraftSection] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<any>(null);
  const [historyFilter, setHistoryFilter] = useState("all");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [published, setPublished] = useState<{ url: string; version: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    if (!id) return;
    const res = await fetchReview(id);
    if (!res) {
      navigate("/proposals");
      return;
    }
    setData(res);
    setProposal(res.proposal);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validation = data?.validation;
  const brand = data?.tenantBrand;
  const shareUrl = published?.url || proposal?.publishedUrl || data?.shareUrl || "";

  const sections = useMemo(
    () => (proposal?.sections || []).slice().sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
    [proposal?.sections]
  );

  const needsReview = sections.filter((s: any) => s.origin === "ai" && !s.approved && !s.hidden).length;

  // Any change to the sections is persisted immediately — the review screen has
  // no separate "save" step in the concept, and a half-saved review is worse
  // than no review at all.
  const persist = async (next: any[]) => {
    if (!id) return;
    setProposal((p: any) => ({ ...p, sections: next }));
    setSaving(true);
    await updateProposal(id, { sections: next });
    setSaving(false);
    setHistory(null);
    await load();
  };

  const toggleApprove = (sectionId: string) =>
    persist(sections.map((s: any) => (s.id === sectionId ? { ...s, approved: !s.approved } : s)));

  const toggleHidden = (sectionId: string) =>
    persist(sections.map((s: any) => (s.id === sectionId ? { ...s, hidden: !s.hidden } : s)));

  const openEditor = (section: any) => {
    setEditingId(section.id);
    setDraftSection(JSON.parse(JSON.stringify(section)));
  };

  const saveEditor = async () => {
    if (!draftSection) return;
    // Editing a section by hand is the BD's work — it stops being AI copy
    // awaiting review and is marked approved by the act of editing it.
    const next = sections.map((s: any) =>
      s.id === draftSection.id ? { ...draftSection, approved: true, origin: s.origin === "ai" ? "ai" : s.origin } : s
    );
    setEditingId(null);
    setDraftSection(null);
    await persist(next);
  };

  const openHistory = async () => {
    setView("history");
    if (history || !id) return;
    try {
      const res = await axiosInstance.get(`/proposals/${id}/versions`);
      setHistory(res.data);
    } catch {
      toast.error("Could not load the proposal history");
    }
  };

  const doPublish = async () => {
    if (!id) return;
    const res = await publishProposal(id);
    if (!res) return;
    if (res.error) {
      setData((d: any) => ({ ...d, validation: res.validation }));
      return;
    }
    setPublished({ url: res.shareUrl, version: res.version });
    setHistory(null);
    await load();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading || !proposal) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "grid", placeItems: "center", color: C.inkSoft, fontFamily: "Manrope, system-ui, sans-serif" }}>
        <FontLink />
        <p style={{ fontSize: 13.5, fontWeight: 700 }}>Loading the proposal for review…</p>
      </div>
    );
  }

  const status = proposal.status || "Draft";
  const tone = STATUS_TONE[status] || DRAFT_TONE;
  const clientName =
    proposal.clientInfo?.company || proposal.clientInfo?.name ||
    proposal.client?.company || proposal.client?.name || "";
  const mark = (brand?.companyName || proposal.title || "CR").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();

  // Events for the history timeline, built from what the proposal actually
  // recorded — activity entries plus each published version.
  const events = [
    ...((history?.activity || []).map((a: any, i: number) => ({
      id: `a${i}`,
      actor: actorOf(a.action),
      time: timeOf(a.at),
      date: new Date(a.at).toLocaleDateString(),
      title: labelForAction(a.action, a.version),
      detail: a.note || "—",
      by: a.byName || "",
    })) as any[]),
    ...((history?.versions || []).map((v: any) => ({
      id: `v${v.version}`,
      actor: "human",
      time: timeOf(v.publishedAt || v.updatedAt),
      date: new Date(v.publishedAt || v.updatedAt).toLocaleDateString(),
      title: `Published version ${v.version}`,
      detail: `${v.sectionCount} sections published by ${v.updatedByName || "—"}.`,
      by: v.updatedByName || "",
    })) as any[]),
  ];
  const visibleEvents = events.filter((e) => historyFilter === "all" || e.actor === historyFilter);

  const previewNode = (
    <InfographicTemplate proposal={proposal} persona={data?.accountPersona} mode="preview" />
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.ink, fontFamily: "Manrope, system-ui, sans-serif", colorScheme: "light" }}>
      <FontLink />

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 32px", background: C.card, borderBottom: `1px solid ${C.line}`,
          gap: 20, flexWrap: "wrap", position: "sticky", top: 0, zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
          <button
            onClick={() => navigate("/proposals")}
            title="Back to proposals"
            style={{
              width: 32, height: 32, borderRadius: 8, background: C.ink, border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontWeight: 800, fontSize: 13, cursor: "pointer", flex: "0 0 auto",
            }}
          >
            {mark}
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, fontFamily: C.mono, color: C.inkFaint }}>
              {view === "review" ? "REVIEWING PROPOSAL — INLINE VIEW" : view === "history" ? "COMBINED HISTORY" : "CLIENT VIEW — EXACTLY WHAT THEY RECEIVE"}
            </div>
            <div style={{ fontSize: 14.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span>{proposal.title}</span>
              <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 100, background: tone.bg, color: tone.fg }}>
                {status}
              </span>
              <span style={{ fontSize: 11, fontFamily: C.mono, color: C.inkFaint }}>v{proposal.version || 1}</span>
              {data?.hasUnpublishedChanges && (
                <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 100, background: C.aiSoft, color: C.ai }}>
                  UNPUBLISHED CHANGES
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, color: C.inkFaint }}>
            {saving
              ? "Saving…"
              : needsReview > 0
              ? `${needsReview} section${needsReview > 1 ? "s" : ""} need review · rest reused from your library`
              : "All sections reviewed"}
          </span>
          {view === "review" ? (
            <>
              <NavBtn onClick={() => setView("client")}>Client view</NavBtn>
              <NavBtn onClick={openHistory}>View history</NavBtn>
              <NavBtn onClick={() => navigate(`/proposal-builder?id=${proposal._id}`)}>Open builder</NavBtn>
            </>
          ) : (
            <NavBtn onClick={() => setView("review")}>Back to proposal</NavBtn>
          )}
          {shareUrl && status === "Published" && (
            <NavBtn onClick={copyLink}>{copied ? "Link copied" : "Copy link"}</NavBtn>
          )}
          <button
            onClick={doPublish}
            disabled={isPublishing || !validation?.ready}
            title={validation?.ready ? "" : "Required information is still missing"}
            style={{
              fontSize: 13, fontWeight: 700, color: "#fff",
              background: validation?.ready ? C.ink : "#C5C9D0",
              border: "none", borderRadius: 8, padding: "10px 18px",
              cursor: validation?.ready && !isPublishing ? "pointer" : "not-allowed",
            }}
          >
            {isPublishing ? "Publishing…" : status === "Published" ? "Publish update" : "Approve & get link"}
          </button>
        </div>
      </div>

      {/* ── Review (inline) ─────────────────────────────────────────────── */}
      {view === "review" && (
        <div>
          {/* Publishing gate — what still has to be filled in */}
          {validation && !validation.ready && (
            <Band bg={C.card}>
              <div
                style={{
                  background: C.dangerSoft, border: `1px solid ${C.dangerLine}`, borderRadius: 10,
                  padding: "14px 16px", display: "flex", gap: 14, justifyContent: "space-between",
                  alignItems: "flex-start", flexWrap: "wrap",
                }}
              >
                <div>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: C.danger }}>
                    {validation.required.length} required item{validation.required.length > 1 ? "s" : ""} before this can be published
                  </p>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5, color: "#7A2E22", lineHeight: 1.6 }}>
                    {validation.required.map((r: any) => (
                      <li key={r.key}>
                        <b>{r.label}</b> — {r.hint}
                      </li>
                    ))}
                  </ul>
                </div>
                <span style={{ fontSize: 12, fontFamily: C.mono, color: C.inkFaint }}>{validation.completeness}% COMPLETE</span>
              </div>

              {validation.optional?.length > 0 && (
                <div
                  style={{
                    marginTop: 12, background: C.aiBox, border: `1px solid ${C.aiBoxLine}`, borderRadius: 10,
                    padding: "12px 16px", fontSize: 12.5, color: "#5C4A1E",
                  }}
                >
                  <b>Worth adding:</b>{" "}
                  {validation.optional.map((r: any) => r.label).join(" · ")}
                </div>
              )}
            </Band>
          )}

          {validation?.ready && status !== "Published" && (
            <Band bg={C.card}>
              <div
                style={{
                  background: C.okSoft, border: `1px solid ${C.okLine}`, borderRadius: 10,
                  padding: "12px 16px", fontSize: 13, color: "#1F5C39", fontWeight: 600,
                }}
              >
                Everything required is in place — publishing will generate the client link on
                {brand?.companyName ? ` ${brand.companyName}'s` : " this brand's"} domain.
              </div>
            </Band>
          )}

          {sections.map((s: any, i: number) => {
            const def = getInfographicSection(s.type);
            const isAi = s.origin === "ai";
            const isManual = s.origin === "manual";
            const isMoney = s.type === "pricing";
            const bg = isMoney ? C.moneyBand : i % 2 === 1 ? C.band : C.card;
            const editing = editingId === s.id;

            return (
              <Band key={s.id} bg={bg} dim={s.hidden}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <Badge tone={isAi ? "ai" : isManual ? "manual" : "ok"}>
                      {isAi
                        ? s.approved ? "AI-WRITTEN — APPROVED" : "AI-WRITTEN — REVIEW"
                        : isManual ? "ADDED BY YOU" : `REUSED — ${(def?.title || s.type).toUpperCase()} TEMPLATE`}
                    </Badge>
                    {s.hidden && <Badge tone="manual">HIDDEN FROM CLIENT</Badge>}
                    {DATA_ONLY.includes(s.type) && (
                      <Badge tone="manual">{DATA_ONLY_NOTE[s.type]}</Badge>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {isAi && (
                      <Pill tone={s.approved ? "ok" : "plain"} onClick={() => toggleApprove(s.id)}>
                        {s.approved ? "Approved" : "Looks good"}
                      </Pill>
                    )}
                    <Pill onClick={() => toggleHidden(s.id)}>{s.hidden ? "Show" : "Hide"}</Pill>
                    <Pill onClick={() => (editing ? setEditingId(null) : openEditor(s))}>{editing ? "Close" : "Edit"}</Pill>
                  </div>
                </div>

                {/* AI copy sits in the amber review frame until it is approved */}
                <div
                  style={
                    isAi && !s.approved
                      ? { border: `1.5px dashed ${C.aiBoxLine}`, borderRadius: 12, padding: "18px 20px", background: C.aiBox }
                      : undefined
                  }
                >
                  <SectionSummary section={s} />
                </div>

                {editing && draftSection && (
                  <div style={{ marginTop: 16, background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: "18px 20px" }}>
                    <p style={{ margin: "0 0 14px", fontSize: 13, color: C.inkSoft }}>
                      Editing <b style={{ color: C.ink }}>{s.title || def?.title}</b> — this changes this proposal only.
                    </p>
                    <input
                      value={draftSection.title || ""}
                      onChange={(e) => setDraftSection({ ...draftSection, title: e.target.value })}
                      placeholder="Section heading"
                      style={{ width: "100%", fontSize: 14, fontWeight: 700, padding: "9px 11px", border: `1px solid ${C.line}`, borderRadius: 8, marginBottom: 16 }}
                    />
                    <InfographicSectionEditor
                      section={draftSection}
                      onUpdate={(u: any) => setDraftSection({ ...draftSection, ...u })}
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                      <Pill tone="dark" onClick={saveEditor}>Save</Pill>
                      <Pill onClick={() => { setEditingId(null); setDraftSection(null); }}>Cancel</Pill>
                    </div>
                  </div>
                )}
              </Band>
            );
          })}

          <div style={{ padding: "26px 32px 60px", textAlign: "center", fontSize: 12.5, color: C.inkFaint }}>
            {sections.length} sections · {needsReview} awaiting your review ·{" "}
            <button onClick={() => setView("client")} style={{ background: "none", border: "none", color: C.ink, fontWeight: 700, cursor: "pointer", fontSize: 12.5 }}>
              see it as the client will
            </button>
          </div>
        </div>
      )}

      {/* ── Client view ─────────────────────────────────────────────────── */}
      {view === "client" && (
        <div style={{ padding: "18px 0 40px" }}>
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 16 }}>
            {(["desktop", "mobile"] as Device[]).map((d) => (
              <button
                key={d}
                onClick={() => setDevice(d)}
                style={{
                  fontSize: 12.5, fontWeight: 700, padding: "8px 14px", borderRadius: 100,
                  border: `1px solid ${device === d ? C.ink : C.line}`,
                  background: device === d ? C.ink : C.card,
                  color: device === d ? "#fff" : C.inkSoft, cursor: "pointer",
                }}
              >
                {d === "desktop" ? "Desktop" : "Mobile"}
              </button>
            ))}
          </div>
          <div
            style={{
              margin: "0 auto", background: "#fff", borderRadius: 12, overflow: "hidden",
              border: `1px solid ${C.line}`, width: device === "mobile" ? 390 : "min(1180px, calc(100% - 48px))",
              maxWidth: "100%",
            }}
          >
            {previewNode}
          </div>
        </div>
      )}

      {/* ── History ─────────────────────────────────────────────────────── */}
      {view === "history" && (
        <div style={{ maxWidth: 900, margin: "0 auto", width: "100%", padding: "32px 32px 60px" }}>
          <div style={{ fontSize: 13.5, color: C.inkSoft, marginBottom: 22 }}>
            This proposal was shaped by{" "}
            <b style={{ color: C.ink }}>{events.filter((e) => e.actor === "system").length} system decisions</b>,{" "}
            <b style={{ color: C.ink }}>{sections.filter((s: any) => s.origin === "ai").length} AI-drafted sections</b> and{" "}
            <b style={{ color: C.ink }}>{events.filter((e) => e.actor === "human").length} of your actions</b> — start to finish,{" "}
            {events.length} events.
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 26, flexWrap: "wrap" }}>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setHistoryFilter(f.key)}
                style={{
                  fontSize: 12.5, fontWeight: 700, padding: "8px 14px", borderRadius: 100,
                  border: `1px solid ${historyFilter === f.key ? C.ink : C.line}`,
                  background: historyFilter === f.key ? C.ink : C.card,
                  color: historyFilter === f.key ? "#fff" : C.inkSoft, cursor: "pointer",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div style={{ position: "relative", paddingLeft: 22 }}>
            <div style={{ position: "absolute", left: 5, top: 6, bottom: 6, width: 2, background: C.line }} />
            <div>
              {visibleEvents.length === 0 && (
                <p style={{ fontSize: 13, color: C.inkFaint }}>Nothing recorded for this filter yet.</p>
              )}
              {visibleEvents.map((ev) => {
                const a = ACTOR[ev.actor] || HUMAN_ACTOR;
                const open = expanded === ev.id;
                return (
                  <div key={ev.id} style={{ position: "relative", paddingBottom: 22 }}>
                    <div style={{ position: "absolute", left: -22, top: 4, width: 12, height: 12, borderRadius: "50%", background: a.color, border: `2px solid ${C.bg}` }} />
                    <div
                      onClick={() => setExpanded(open ? null : ev.id)}
                      style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                          <span style={{ fontSize: 10.5, fontWeight: 800, color: "#fff", background: a.color, padding: "3px 8px", borderRadius: 100, flex: "0 0 auto" }}>
                            {a.label}
                          </span>
                          <span style={{ fontSize: 13.5, fontWeight: 700 }}>{ev.title}</span>
                        </div>
                        <span style={{ fontSize: 12, fontFamily: C.mono, color: C.inkFaint, flex: "0 0 auto" }}>
                          {ev.date} {ev.time}
                        </span>
                      </div>
                      {open && (
                        <div style={{ marginTop: 10, fontSize: 13, color: C.inkSoft, lineHeight: 1.55 }}>
                          {ev.detail}
                          {ev.by && <div style={{ marginTop: 6, fontSize: 12, color: C.inkFaint }}>by {ev.by}</div>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Published confirmation ──────────────────────────────────────── */}
      {published && (
        <div
          onClick={() => setPublished(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(20,24,28,.5)", display: "grid", placeItems: "center", padding: 24, zIndex: 60 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: C.card, borderRadius: 14, maxWidth: 440, width: "100%", padding: "24px 26px" }}>
            <p style={{ margin: 0, fontSize: 11, fontFamily: C.mono, color: C.ok }}>LINK READY</p>
            <h3 style={{ margin: "6px 0 4px", fontSize: 17 }}>Version {published.version} is live for the client</h3>
            <p style={{ margin: 0, fontSize: 13, color: C.inkSoft }}>
              Paste this into your cover letter or message thread.
            </p>
            <p style={{ marginTop: 14, fontSize: 12.5, fontFamily: C.mono, background: C.bg, border: `1px solid ${C.line}`, borderRadius: 8, padding: "10px 12px", wordBreak: "break-all" }}>
              {published.url}
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <Pill tone="dark" onClick={copyLink}>{copied ? "Copied" : "Copy link"}</Pill>
              <a href={published.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                <Pill>Open</Pill>
              </a>
              <Pill onClick={() => setPublished(null)}>Close</Pill>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── helpers ─────────────────────────────────────────────────────────────────
const FontLink = () => (
  <style>{`@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>
);

const NavBtn = ({ children, onClick }: { children: any; onClick: () => void }) => (
  <button
    onClick={onClick}
    style={{
      fontSize: 13, fontWeight: 700, color: C.inkSoft, border: `1px solid ${C.line}`,
      borderRadius: 8, padding: "10px 16px", cursor: "pointer", background: C.card, whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

const Band = ({ bg, children, dim }: { bg: string; children: any; dim?: boolean }) => (
  <div style={{ padding: "30px 40px", background: bg, borderBottom: `1px solid ${C.line}`, opacity: dim ? 0.55 : 1 }}>
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>{children}</div>
  </div>
);

const labelForAction = (action = "", version = 0) => {
  const map: Record<string, string> = {
    saved: "Proposal saved",
    "edited-after-publish": "Edited after publishing — not live until republished",
    published: "Approved & generated link",
    republished: `Republished as version ${version}`,
  };
  return map[action] || action.replace(/-/g, " ");
};

// A compact, readable rendering of whatever a section holds — the review screen
// shows the words, not the client-facing styling (that is the Client view).
const SectionSummary = ({ section }: { section: any }) => {
  const c = section.content || {};
  const def = getInfographicSection(section.type);
  const heading = section.title || def?.title || section.type;
  const txt = (v: any) => String(v ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const arr = (v: any) => (Array.isArray(v) ? v : []);
  const rowText = (r: any) => (typeof r === "string" ? r : txt(r?.title || r?.name || r?.label || r?.quote || r?.text));

  const lists: [string, any[]][] = [
    ["", arr(c.points)],
    ["", arr(c.items)],
    ["", arr(c.steps)],
    ["", arr(c.pillars)],
    ["", arr(c.milestones)],
    ["", arr(c.excluded)],
  ];
  const rows = lists.flatMap(([, v]) => v).filter((r) => rowText(r));
  const body = txt(c.text || c.subheadline || c.note);
  const tiers = arr(c.tiers);
  const gallery = [...arr(c.projects), ...arr(c.bestProjects)].filter((x: any) => x?.image || rowText(x));
  const badges = arr(c.badges).filter((x: any) => txt(x?.value) || txt(x?.label));

  return (
    <>
      <h2 style={{ fontSize: 18, margin: "0 0 10px", fontWeight: 800 }}>{txt(c.headline) || heading}</h2>

      {body && <p style={{ fontSize: 14.5, color: C.inkSoft, margin: 0, maxWidth: "64ch", lineHeight: 1.55 }}>{body}</p>}

      {arr(c.ledes).filter((l: any) => txt(l)).map((l: any, i: number) => (
        <p key={i} style={{ fontSize: 14.5, color: C.inkSoft, margin: "8px 0 0", maxWidth: "64ch", lineHeight: 1.55 }}>{txt(l)}</p>
      ))}

      {badges.length > 0 && (
        <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
          {badges.map((x: any, i: number) => (
            <div key={i} style={{ fontSize: 12.5, color: C.inkFaint }}>
              <b style={{ display: "block", fontSize: 16, color: C.ink }}>{txt(x.value)}</b>
              {txt(x.label)}
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 14 }}>
          {rows.slice(0, 8).map((r: any, i: number) => (
            <div key={i} style={{ flex: "1 1 200px", minWidth: 180, background: C.card, border: `1px solid ${C.line}`, borderRadius: 10, padding: 14 }}>
              <b style={{ fontSize: 13.5 }}>{rowText(r)}</b>
              {typeof r !== "string" && txt(r.description || r.duration || r.format) && (
                <p style={{ fontSize: 12.5, color: C.inkSoft, margin: "6px 0 0" }}>{txt(r.description || r.duration || r.format)}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tiers.length > 0 && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 14, alignItems: "flex-start" }}>
          {tiers.map((t: any, i: number) => (
            <div
              key={i}
              style={{
                flex: t.featured ? "1.4 1 260px" : "1 1 200px", background: C.card,
                border: t.featured ? `2px solid ${C.ok}` : `1px solid ${C.line}`,
                borderRadius: 10, padding: 18, position: "relative",
              }}
            >
              {t.featured && (
                <span style={{ position: "absolute", top: -10, left: 16, background: C.ok, color: "#fff", fontSize: 10.5, fontWeight: 800, padding: "3px 9px", borderRadius: 5 }}>
                  RECOMMENDED
                </span>
              )}
              <div style={{ fontSize: 11.5, color: C.inkFaint, fontWeight: 700, textTransform: "uppercase" }}>{txt(t.name || t.title)}</div>
              <div style={{ fontSize: 22, fontWeight: 800, marginTop: 8 }}>
                {txt(t.price) || "—"}
                {txt(t.unit) && <span style={{ fontSize: 12, fontWeight: 600, color: C.inkFaint }}> {txt(t.unit)}</span>}
              </div>
              <ul style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 12.5, color: C.inkSoft }}>
                {arr(t.features).filter((f: any) => txt(f)).map((f: any, j: number) => (
                  <li key={j}>{rowText(f)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {gallery.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          {gallery.slice(0, 6).map((g: any, i: number) => (
            <div key={i} style={{ width: 128, border: `1px solid ${C.line}`, borderRadius: 10, overflow: "hidden", background: C.card }}>
              <div style={{ height: 78, display: "grid", placeItems: "center", background: "#fff" }}>
                {g.image ? (
                  <img src={g.image} alt={rowText(g)} style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} />
                ) : (
                  <span style={{ color: C.inkFaint, fontSize: 20, fontWeight: 800 }}>{(rowText(g) || "·").slice(0, 1)}</span>
                )}
              </div>
              <p style={{ margin: 0, padding: "7px 9px", fontSize: 11.5, fontWeight: 700 }}>{rowText(g)}</p>
            </div>
          ))}
        </div>
      )}

      {arr(c.highlights).filter((h: any) => txt(h.value) || txt(h.label)).length > 0 && (
        <div style={{ display: "flex", gap: 18, marginTop: 14, flexWrap: "wrap" }}>
          {arr(c.highlights).filter((h: any) => txt(h.value) || txt(h.label)).map((h: any, i: number) => (
            <div key={i} style={{ fontSize: 12, color: C.inkFaint }}>
              <b style={{ display: "block", fontSize: 15, color: C.ink }}>{txt(h.value)}</b>
              {txt(h.label)}
            </div>
          ))}
        </div>
      )}

      {(txt(c.email) || txt(c.phone) || txt(c.company) || txt(c.website)) && (
        <p style={{ fontSize: 13, color: C.inkSoft, marginTop: 12 }}>
          {[txt(c.company), txt(c.name), txt(c.email), txt(c.phone), txt(c.website)].filter(Boolean).join(" · ")}
        </p>
      )}

      {txt(c.buttonText) && (
        <p style={{ fontSize: 12.5, color: C.inkFaint, marginTop: 12 }}>
          Button: <b style={{ color: C.ink }}>{txt(c.buttonText)}</b>
          {txt(c.buttonUrl) ? ` → ${txt(c.buttonUrl)}` : " — no link set"}
        </p>
      )}

      {!body && !rows.length && !tiers.length && !gallery.length && !badges.length && (
        <p style={{ fontSize: 13, color: C.inkFaint, margin: "8px 0 0", fontStyle: "italic" }}>
          Nothing written here yet — this section will not appear for the client.
        </p>
      )}
    </>
  );
};

export default ProposalReviewPage;
